import env from 'env'
import { ROUTES } from 'constants/routes'
import { getLang } from 'redux-app/selectors/localization'
import {
  getUserId,
  getSubscriptionName,
  getUserActivePlanId,
  getActivePlanPrice,
  getActiveSubscriptionId,
  getActiveSubscriptionContractId,
} from 'redux-app/selectors/user'
import { 
  getSelectedPlanId, 
  getSelectedPlanPrice, 
  getSelectedPlanName, 
  isSelectedPlanAvailable } from 'redux-app/selectors/subscription'
import { isUserAuthorizated } from 'redux-app/selectors/auth'
import {
  VIEW_PAYLOAD_KEYS,
  LINK_PAYLOAD_KEYS,
  CUSTOMER_STATUSES,
  PAGE_CONTENT_TYPES,
  SITE_SECTIONS,
  SITE_TYPE,
  PAGE_TEMPLATE_NAMES,
} from './constants'
import { getPageName, getPageNameParams, getFirstOrSecondCreator, getNormalizedStringValue } from './utils'
import { SUBSCRIPTION_PLAN_TYPE } from 'constants/subscription'

interface IAnalyticsService {
  init: (params: { account: string, profile: string, environment: string }) => Promise<{}>,
  view: (payload: { [key: string]: string }) => void,
  link: (payload: { [key: string]: string }) => void,
}

const getNormalizedLang = state => getNormalizedStringValue(getLang(state))
const getNormalizedActivePlanName = state => getNormalizedStringValue(getSubscriptionName(state))
const getNormalizedSelectedPlanName = state => getNormalizedStringValue(getSelectedPlanName(state))

class Analytics {
  private readonly analyticsService: IAnalyticsService = null
  private getState = null

  constructor (analyticsService: IAnalyticsService) {
    this.analyticsService = analyticsService
  }

  public init (getState) {
    this.getState = getState
    return this.analyticsService.init({
      account: env.analytics.account,
      profile: env.analytics.profile,
      environment: env.analytics.environment,
    })
  }

  private getPayloadForOtherPage = () => {
    const getValueForAuthorized = getFirstOrSecondCreator(isUserAuthorizated(this.getState()))
    return {
      [VIEW_PAYLOAD_KEYS.customerId]: getValueForAuthorized(getUserId(this.getState())),
      [VIEW_PAYLOAD_KEYS.customerStatus]:
        getValueForAuthorized(CUSTOMER_STATUSES.loggedIn, CUSTOMER_STATUSES.notLoggedIn),
      [VIEW_PAYLOAD_KEYS.customerSubscriptionPlanType]:
        getValueForAuthorized(getNormalizedActivePlanName(this.getState())),
      [VIEW_PAYLOAD_KEYS.languageCode]: getNormalizedLang(this.getState()),
      [VIEW_PAYLOAD_KEYS.siteCurrency]: 'usd',
      [VIEW_PAYLOAD_KEYS.siteRegion]: 'us',
      [VIEW_PAYLOAD_KEYS.siteType]: SITE_TYPE,
    }
  }

  private getPayloadForCheckoutPage = () => {
    const getValueForAuthorized = getFirstOrSecondCreator(isUserAuthorizated(this.getState()))
    return {
      [VIEW_PAYLOAD_KEYS.customerId]: getValueForAuthorized(getUserId(this.getState())),
      [VIEW_PAYLOAD_KEYS.customerStatus]:
        getValueForAuthorized(CUSTOMER_STATUSES.loggedIn, CUSTOMER_STATUSES.notLoggedIn),
      [VIEW_PAYLOAD_KEYS.customerSubscriptionPlanType]:
        getValueForAuthorized(getNormalizedActivePlanName(this.getState())),
      [VIEW_PAYLOAD_KEYS.languageCode]: getNormalizedLang(this.getState()),
      [VIEW_PAYLOAD_KEYS.siteCurrency]: 'usd',
      [VIEW_PAYLOAD_KEYS.siteRegion]: 'us',
      [VIEW_PAYLOAD_KEYS.siteType]: SITE_TYPE,
      [VIEW_PAYLOAD_KEYS.subscriptionPlanName]:
        getValueForAuthorized(getNormalizedActivePlanName(this.getState())),
      [VIEW_PAYLOAD_KEYS.subscriptionPlanId]: getValueForAuthorized(getUserActivePlanId(this.getState())),
      [VIEW_PAYLOAD_KEYS.subscriptionPlanListPrice]: getValueForAuthorized(getActivePlanPrice(this.getState())),
      [VIEW_PAYLOAD_KEYS.subscriptionPlanNetPrice]: getValueForAuthorized(getActivePlanPrice(this.getState())),
      [VIEW_PAYLOAD_KEYS.subscriptionId]: getValueForAuthorized(getActiveSubscriptionId(this.getState())),
      [VIEW_PAYLOAD_KEYS.subscriptionContractId]:
        getValueForAuthorized(getActiveSubscriptionContractId(this.getState())),
    }
  }

  private getPayloadForOrderConfirmationPage = () => {
    const getValueForAuthorized = getFirstOrSecondCreator(isUserAuthorizated(this.getState()))
    return {
      [VIEW_PAYLOAD_KEYS.customerId]: getValueForAuthorized(getUserId(this.getState())),
      [VIEW_PAYLOAD_KEYS.customerStatus]:
        getValueForAuthorized(CUSTOMER_STATUSES.loggedIn, CUSTOMER_STATUSES.notLoggedIn),
      [VIEW_PAYLOAD_KEYS.customerSubscriptionPlanType]:
        getValueForAuthorized(getNormalizedActivePlanName(this.getState())),
      [VIEW_PAYLOAD_KEYS.languageCode]: getNormalizedLang(this.getState()),
      [VIEW_PAYLOAD_KEYS.orderCouponCode]: '',
      [VIEW_PAYLOAD_KEYS.orderCurrency]: 'usd',
      [VIEW_PAYLOAD_KEYS.orderDiscount]: '',
      [VIEW_PAYLOAD_KEYS.orderId]: getValueForAuthorized(getActiveSubscriptionId(this.getState())),
      [VIEW_PAYLOAD_KEYS.orderPaymentType]: '',
      [VIEW_PAYLOAD_KEYS.orderSubtotal]: '',
      [VIEW_PAYLOAD_KEYS.orderTax]: '',
      [VIEW_PAYLOAD_KEYS.orderTotal]: '',
      [VIEW_PAYLOAD_KEYS.siteCurrency]: 'usd',
      [VIEW_PAYLOAD_KEYS.siteRegion]: 'us',
      [VIEW_PAYLOAD_KEYS.siteType]: SITE_TYPE,
      [VIEW_PAYLOAD_KEYS.subscriptionPlanName]:
        getValueForAuthorized(getNormalizedActivePlanName(this.getState())),
      [VIEW_PAYLOAD_KEYS.subscriptionPlanId]: getValueForAuthorized(getUserActivePlanId(this.getState())),
      [VIEW_PAYLOAD_KEYS.subscriptionPlanListPrice]: getValueForAuthorized(getActivePlanPrice(this.getState())),
      [VIEW_PAYLOAD_KEYS.subscriptionPlanNetPrice]: getValueForAuthorized(getActivePlanPrice(this.getState())),
      [VIEW_PAYLOAD_KEYS.subscriptionId]: getValueForAuthorized(getActiveSubscriptionId(this.getState())),
      [VIEW_PAYLOAD_KEYS.subscriptionContractId]:
        getValueForAuthorized(getActiveSubscriptionContractId(this.getState())),
      [VIEW_PAYLOAD_KEYS.eventName]: 'my account',
      [VIEW_PAYLOAD_KEYS.eventType]: 'subscription sign up success',
    }
  }

  // view
  public openLoginPage () {
    this.analyticsService.view({
      ...this.getPayloadForOtherPage(),
      [VIEW_PAYLOAD_KEYS.pageContentType]: PAGE_CONTENT_TYPES.login,
      [VIEW_PAYLOAD_KEYS.pageName]: getPageName(getPageNameParams()),
      [VIEW_PAYLOAD_KEYS.pageTemplateName]: PAGE_TEMPLATE_NAMES.login,
      [VIEW_PAYLOAD_KEYS.siteSection]: SITE_SECTIONS.login,
    })
  }

  public openContactPage () {
    this.analyticsService.view({
      ...this.getPayloadForOtherPage(),
      [VIEW_PAYLOAD_KEYS.pageContentType]: PAGE_CONTENT_TYPES.contact,
      [VIEW_PAYLOAD_KEYS.pageName]: getPageName(getPageNameParams()),
      [VIEW_PAYLOAD_KEYS.pageTemplateName]: PAGE_TEMPLATE_NAMES.contactUs,
      [VIEW_PAYLOAD_KEYS.siteSection]: SITE_SECTIONS.contact,
    })
  }

  public openHomePage () {
    this.analyticsService.view({
      ...this.getPayloadForOtherPage(),
      [VIEW_PAYLOAD_KEYS.pageContentType]: PAGE_CONTENT_TYPES.home,
      [VIEW_PAYLOAD_KEYS.pageName]: getPageName(getPageNameParams()),
      [VIEW_PAYLOAD_KEYS.pageTemplateName]: PAGE_TEMPLATE_NAMES.home,
      [VIEW_PAYLOAD_KEYS.siteSection]: SITE_SECTIONS.home,
    })
  }

  public openMyProfilePage () {
    this.analyticsService.view({
      ...this.getPayloadForOtherPage(),
      [VIEW_PAYLOAD_KEYS.pageContentType]: PAGE_CONTENT_TYPES.myAccount,
      [VIEW_PAYLOAD_KEYS.pageName]: getPageName(getPageNameParams()),
      [VIEW_PAYLOAD_KEYS.pageTemplateName]: PAGE_TEMPLATE_NAMES.myAccount,
      [VIEW_PAYLOAD_KEYS.siteSection]: SITE_SECTIONS.myAccount,
    })
  }

  public openSignUpPage () {
    this.analyticsService.view({
      ...this.getPayloadForCheckoutPage(),
      [VIEW_PAYLOAD_KEYS.pageContentType]: PAGE_CONTENT_TYPES.checkout,
      [VIEW_PAYLOAD_KEYS.pageName]: getPageName(getPageNameParams()),
      [VIEW_PAYLOAD_KEYS.pageTemplateName]: PAGE_TEMPLATE_NAMES.signUp,
      [VIEW_PAYLOAD_KEYS.siteSection]: SITE_SECTIONS.signUp,
      [VIEW_PAYLOAD_KEYS.subscriptionPlanId]: getSelectedPlanId(this.getState()),
      [VIEW_PAYLOAD_KEYS.subscriptionPlanName]: getNormalizedSelectedPlanName(this.getState()),
      [VIEW_PAYLOAD_KEYS.subscriptionPlanListPrice]: getSelectedPlanPrice(this.getState()),
      [VIEW_PAYLOAD_KEYS.subscriptionPlanNetPrice]: getSelectedPlanPrice(this.getState()),
    })
  }

  public openCreatePaymentAndShippingPage () {
    this.analyticsService.view({
      ...this.getPayloadForCheckoutPage(),
      [VIEW_PAYLOAD_KEYS.pageContentType]: PAGE_CONTENT_TYPES.checkout,
      [VIEW_PAYLOAD_KEYS.pageName]: getPageName(getPageNameParams({ sectionType: SITE_SECTIONS.paymentAndShipping })),
      [VIEW_PAYLOAD_KEYS.pageTemplateName]: PAGE_TEMPLATE_NAMES.paymentAndShipping,
      [VIEW_PAYLOAD_KEYS.siteSection]: SITE_SECTIONS.paymentAndShipping,
    })
  }

  public openCreateReviewTermsPage () {
    this.analyticsService.view({
      ...this.getPayloadForCheckoutPage(),
      [VIEW_PAYLOAD_KEYS.pageContentType]: PAGE_CONTENT_TYPES.checkout,
      [VIEW_PAYLOAD_KEYS.pageName]: getPageName(getPageNameParams({ sectionType: SITE_SECTIONS.reviewTerms })),
      [VIEW_PAYLOAD_KEYS.pageTemplateName]: PAGE_TEMPLATE_NAMES.reviewTerms,
      [VIEW_PAYLOAD_KEYS.siteSection]: SITE_SECTIONS.reviewTerms,
    })
  }

  public openCreateCongratulationsPage () {
    this.analyticsService.view({
      ...this.getPayloadForCheckoutPage(),
      [VIEW_PAYLOAD_KEYS.pageContentType]: PAGE_CONTENT_TYPES.checkout,
      [VIEW_PAYLOAD_KEYS.pageName]: getPageName(getPageNameParams({ sectionType: SITE_SECTIONS.congratulations })),
      [VIEW_PAYLOAD_KEYS.pageTemplateName]: PAGE_TEMPLATE_NAMES.congratulations,
      [VIEW_PAYLOAD_KEYS.siteSection]: SITE_SECTIONS.congratulations,
    })
  }

  public openSuccessConfirmEmailPage () {
    this.analyticsService.view({
      ...this.getPayloadForOrderConfirmationPage(),
      [VIEW_PAYLOAD_KEYS.pageContentType]: PAGE_CONTENT_TYPES.orderConfirmation,
      [VIEW_PAYLOAD_KEYS.pageName]: getPageName(getPageNameParams()),
      [VIEW_PAYLOAD_KEYS.pageTemplateName]: PAGE_TEMPLATE_NAMES.confirmEmail,
      [VIEW_PAYLOAD_KEYS.siteSection]: SITE_SECTIONS.confirmEmail,
    })
  }

  public viewVideoOnHomePage() {
    this.analyticsService.view({
      ...this.getPayloadForOtherPage(),
      [VIEW_PAYLOAD_KEYS.eventType]: 'video',
      [VIEW_PAYLOAD_KEYS.eventName]: 'watch_video_cta_click',
      [VIEW_PAYLOAD_KEYS.destinationUrl]: '/videos/40fce31ffc36e04167207ba43f2cfcb1.mp4',
      [VIEW_PAYLOAD_KEYS.pageContentType]: PAGE_CONTENT_TYPES.home,
      [VIEW_PAYLOAD_KEYS.pageName]: getPageName(getPageNameParams()),
      [VIEW_PAYLOAD_KEYS.pageTemplateName]: PAGE_TEMPLATE_NAMES.home,
      [VIEW_PAYLOAD_KEYS.siteSection]: SITE_SECTIONS.home,
    })
  }

  private showError ({ sectionType, pageTemplateName, pageContentType, errorCodes }) {
    let getInitialPayload = this.getPayloadForOtherPage
    if (pageContentType === PAGE_CONTENT_TYPES.checkout) {
      getInitialPayload = this.getPayloadForCheckoutPage
    } else if (pageContentType === PAGE_CONTENT_TYPES.orderConfirmation) {
      getInitialPayload = this.getPayloadForOrderConfirmationPage
    }
    this.analyticsService.link({
      ...getInitialPayload(),
      [VIEW_PAYLOAD_KEYS.pageContentType]: pageContentType,
      [VIEW_PAYLOAD_KEYS.pageName]: getPageName(getPageNameParams({ sectionType })),
      [VIEW_PAYLOAD_KEYS.pageTemplateName]: pageTemplateName,
      [VIEW_PAYLOAD_KEYS.siteSection]: sectionType,
      [VIEW_PAYLOAD_KEYS.errorDisplayType]: 'server_side',
      [VIEW_PAYLOAD_KEYS.errorType]: 'form_validation_error',
      [VIEW_PAYLOAD_KEYS.errorMessages]: errorCodes.join(','),
    })
  }

  public showLoginError ({ errorCodes }) {
    this.showError({
      sectionType: PAGE_CONTENT_TYPES.login,
      pageTemplateName: PAGE_TEMPLATE_NAMES.login,
      pageContentType: PAGE_CONTENT_TYPES.login,
      errorCodes,
    })
  }

  public showSignUpError ({ errorCodes }) {
    this.showError({
      sectionType: PAGE_CONTENT_TYPES.checkout,
      pageTemplateName: PAGE_TEMPLATE_NAMES.signUp,
      pageContentType: PAGE_CONTENT_TYPES.checkout,
      errorCodes,
    })
  }

  public showContactUsError ({ errorCodes }) {
    this.showError({
      sectionType: PAGE_CONTENT_TYPES.contact,
      pageTemplateName: PAGE_TEMPLATE_NAMES.contactUs,
      pageContentType: PAGE_CONTENT_TYPES.contact,
      errorCodes,
    })
  }

  public showPaymentAndShippingError ({ errorCodes }) {
    this.showError({
      sectionType: PAGE_CONTENT_TYPES.checkout,
      pageTemplateName: PAGE_TEMPLATE_NAMES.paymentAndShipping,
      pageContentType: PAGE_CONTENT_TYPES.checkout,
      errorCodes,
    })
  }

  public showReviewTermsError ({ errorCodes }) {
    this.showError({
      sectionType: PAGE_CONTENT_TYPES.checkout,
      pageTemplateName: PAGE_TEMPLATE_NAMES.reviewTerms,
      pageContentType: PAGE_CONTENT_TYPES.checkout,
      errorCodes,
    })
  }

  public showCongratulationsError ({ errorCodes }) {
    this.showError({
      sectionType: PAGE_CONTENT_TYPES.checkout,
      pageTemplateName: PAGE_TEMPLATE_NAMES.congratulations,
      pageContentType: PAGE_CONTENT_TYPES.checkout,
      errorCodes,
    })
  }

  public showMyAccountError ({ errorCodes, sectionType }) {
    this.showError({
      sectionType: PAGE_CONTENT_TYPES.myAccount,
      pageTemplateName: PAGE_TEMPLATE_NAMES.myAccount,
      pageContentType: sectionType,
      errorCodes,
    })
  }

  public showConfirmEmailError ({ errorCodes }) {
    this.showError({
      sectionType: PAGE_CONTENT_TYPES.orderConfirmation,
      pageTemplateName: PAGE_TEMPLATE_NAMES.confirmEmail,
      pageContentType: SITE_SECTIONS.confirmEmail,
      errorCodes,
    })
  }

  public showForgotPasswordError ({ errorCodes }) {
    this.showError({
      sectionType: PAGE_CONTENT_TYPES.forgotPassword,
      pageTemplateName: PAGE_TEMPLATE_NAMES.forgotPassword,
      pageContentType: SITE_SECTIONS.forgotPassword,
      errorCodes,
    })
  }

  // link
  public login ({ userId, contentType }) {
    let pageName = null
    if (contentType === PAGE_CONTENT_TYPES.login) {
      pageName = getPageName({
        pageContentType: PAGE_CONTENT_TYPES.login,
        pathname: ROUTES.home.logIn,
        sectionType: SITE_SECTIONS.login,
      })
    } else if (contentType === PAGE_CONTENT_TYPES.checkout) {
      pageName = getPageName({
        pageContentType: PAGE_CONTENT_TYPES.checkout,
        pathname: ROUTES.home.signUp,
        sectionType: SITE_SECTIONS.signUp,
      })
    }
    this.analyticsService.link({
      [LINK_PAYLOAD_KEYS.eventType]: 'log in',
      [LINK_PAYLOAD_KEYS.eventName]: 'logged in',
      [LINK_PAYLOAD_KEYS.pageName]: pageName,
      [LINK_PAYLOAD_KEYS.customerId]: userId,
    })
  }

  public logout () {
    this.analyticsService.link({
      [LINK_PAYLOAD_KEYS.eventType]: 'log in',
      [LINK_PAYLOAD_KEYS.eventName]: 'Sign out',
      [LINK_PAYLOAD_KEYS.pageName]: getPageName(getPageNameParams()),
      [LINK_PAYLOAD_KEYS.customerId]: getUserId(this.getState()),
    })
  }

  public contact () {
    const isAuthorized = isUserAuthorizated(this.getState())
    this.analyticsService.link({
      [LINK_PAYLOAD_KEYS.eventType]: 'contact us',
      [LINK_PAYLOAD_KEYS.eventName]: 'send message',
      [LINK_PAYLOAD_KEYS.pageName]: getPageName(getPageNameParams()),
      [LINK_PAYLOAD_KEYS.customerId]: isAuthorized ? getUserId(this.getState()) : '',
    })
  }

  public homeSelectPlan ({ activationFee }) {

    let array = {
      ...this.getPayloadForOtherPage(),
      [LINK_PAYLOAD_KEYS.eventType]: 'select_plan',
      [LINK_PAYLOAD_KEYS.eventName]: isSelectedPlanAvailable(this.getState()) === true ? 'plan_selected' : 'notify_me',
      [LINK_PAYLOAD_KEYS.subscriptionPlanId]: getSelectedPlanId(this.getState()),
      [LINK_PAYLOAD_KEYS.subscriptionPlanName]: getNormalizedSelectedPlanName(this.getState()),
      [LINK_PAYLOAD_KEYS.subscriptionPlanListPrice]: getSelectedPlanPrice(this.getState()),
      [LINK_PAYLOAD_KEYS.subscriptionPlanNetPrice]: getSelectedPlanPrice(this.getState()),
      [VIEW_PAYLOAD_KEYS.pageContentType]: PAGE_CONTENT_TYPES.home,
      [VIEW_PAYLOAD_KEYS.pageName]: getPageName(getPageNameParams()),
      [VIEW_PAYLOAD_KEYS.pageTemplateName]: PAGE_TEMPLATE_NAMES.home,
      [VIEW_PAYLOAD_KEYS.siteSection]: SITE_SECTIONS.home,
    }

    if (activationFee !== null)
    {
      var fee = {[LINK_PAYLOAD_KEYS.activationFeeCheckbox]: activationFee > 0 ? 'yes' : 'no'}
      array = {...array, ...fee}
    }

    this.analyticsService.link(array)
  }

  public upgradePlan ({ planName }) {
    this.analyticsService.link({
      [LINK_PAYLOAD_KEYS.eventType]: 'my account',
      [LINK_PAYLOAD_KEYS.eventName]: 'upgrade subscription plan',
      [LINK_PAYLOAD_KEYS.upgradePlan]: getNormalizedStringValue(planName),
      [LINK_PAYLOAD_KEYS.pageName]: getPageName(getPageNameParams()),
      [LINK_PAYLOAD_KEYS.customerId]: getUserId(this.getState()),
    })
  }

  public downgradePlan ({ planName }) {
    this.analyticsService.link({
      [LINK_PAYLOAD_KEYS.eventType]: 'my account',
      [LINK_PAYLOAD_KEYS.eventName]: 'downgrade subscription plan',
      [LINK_PAYLOAD_KEYS.downgradePlan]: getNormalizedStringValue(planName),
      [LINK_PAYLOAD_KEYS.pageName]: getPageName(getPageNameParams()),
      [LINK_PAYLOAD_KEYS.customerId]: getUserId(this.getState()),
    })
  }

  public renewPlan ({ planName, terms, price }) {
    this.analyticsService.link({
      [LINK_PAYLOAD_KEYS.eventType]: 'my account',
      [LINK_PAYLOAD_KEYS.eventName]: 'subscription_extended',
      [LINK_PAYLOAD_KEYS.renewalTerm]: terms === SUBSCRIPTION_PLAN_TYPE.contract ? '2_year' : 'monthly',
      [LINK_PAYLOAD_KEYS.subscriptionPlanId]: getSelectedPlanId(this.getState()),
      [LINK_PAYLOAD_KEYS.subscriptionPlanName]: getNormalizedStringValue(planName),
      [LINK_PAYLOAD_KEYS.subscriptionPlanListPrice]: price,
      [LINK_PAYLOAD_KEYS.subscriptionPlanNetPrice]: price,
      [LINK_PAYLOAD_KEYS.extendPlan]: getNormalizedStringValue(planName),
      [LINK_PAYLOAD_KEYS.pageName]: getPageName(getPageNameParams()),
      [LINK_PAYLOAD_KEYS.customerId]: getUserId(this.getState()),
    })
  }

  public enrollPlan ({ planName }) {
    this.analyticsService.link({
      [LINK_PAYLOAD_KEYS.eventType]: 'my account',
      [LINK_PAYLOAD_KEYS.eventName]: 'enroll_subscription_plan',
      [LINK_PAYLOAD_KEYS.enrollPlan]: getNormalizedStringValue(planName),
      [LINK_PAYLOAD_KEYS.subscriptionPlanId]: getSelectedPlanId(this.getState()),
      [LINK_PAYLOAD_KEYS.subscriptionPlanName]: getNormalizedSelectedPlanName(this.getState()),
      [LINK_PAYLOAD_KEYS.subscriptionPlanListPrice]: getSelectedPlanPrice(this.getState()),
      [LINK_PAYLOAD_KEYS.subscriptionPlanNetPrice]: getSelectedPlanPrice(this.getState()),
      [LINK_PAYLOAD_KEYS.pageName]: getPageName(getPageNameParams()),
      [LINK_PAYLOAD_KEYS.customerId]: getUserId(this.getState()),
    })
  }

  public cancelSubscription () {
    this.analyticsService.link({
      [LINK_PAYLOAD_KEYS.eventType]: 'my account',
      [LINK_PAYLOAD_KEYS.eventName]: 'cancel subscription',
      [LINK_PAYLOAD_KEYS.pageName]: getPageName(getPageNameParams()),
      [LINK_PAYLOAD_KEYS.customerId]: getUserId(this.getState()),
    })
  }

  public saveChanges ({ sectionName }) {
    this.analyticsService.link({
      [LINK_PAYLOAD_KEYS.eventType]: 'my account',
      [LINK_PAYLOAD_KEYS.eventName]: `save changes:${sectionName}`,
      [LINK_PAYLOAD_KEYS.pageName]: getPageName(getPageNameParams({ sectionType: sectionName })),
      [LINK_PAYLOAD_KEYS.customerId]: getUserId(this.getState()),
    })
  }

  public forgotPassword () {
    this.analyticsService.link({
      [LINK_PAYLOAD_KEYS.eventType]: 'login',
      [LINK_PAYLOAD_KEYS.eventName]: 'forgot password',
      [LINK_PAYLOAD_KEYS.pageName]: getPageName(getPageNameParams()),
      [LINK_PAYLOAD_KEYS.customerId]: getUserId(this.getState()),
    })
  }
}

export default Analytics
